import { create } from 'zustand';
import { JwtService } from '@src/api/common/JwtService';
import { UserService, type IUserData } from '@src/api/common/UserService';

type SessionIdentity = {
  givenName: string;
  displayName: string;
};

type SyncOptions = {
  incrementEpoch?: boolean; 
};

interface AuthSessionState {
  user: IUserData | null;
  token: string | null;
  identity: SessionIdentity;
  isViewer: boolean;
  isAdmin: boolean;
  isImpersonating: boolean;
  sessionEpoch: number;
  syncFromCookies: (options?: SyncOptions) => void;
}

const defaultIdentity: SessionIdentity = {
  givenName: 'User',
  displayName: 'User',
};

const hasRole = (user: IUserData | null, role: string) => {
  if (!user?.allRoles) {
    return false;
  }

  const roles = Array.isArray(user.allRoles)
    ? user.allRoles
    : String(user.allRoles)
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

  return roles.includes(role);
};

const buildAuthSessionSnapshot = () => {
  const token = UserService.GetToken();
  const user = UserService.GetUser();

  //TODO: [jwtService should be at backend] consider moving token parsing to 
  // backend and just providing necessary user info and roles in the cookie 
  // to avoid potential issues with token parsing on frontend and 
  // to centralize token handling logic

  const userFromToken = token ? JwtService.GetUserFromToken(token) : null;
  const storedUserName =
    typeof window !== 'undefined' ? localStorage.getItem('userName') : null;

  const isImpersonating = UserService.HasImpersonationSwitch();

  const switchUser = UserService.GetSwitchUser();
  const activeUser = UserService.GetUser();
  const actorUser = isImpersonating ? switchUser : activeUser;

  const actorIsAdmin = hasRole(actorUser, 'AZU_CTE_ADMIN');
  const isViewer = hasRole(actorUser, 'AZU_CTE_VIEWER');
  
  if (userFromToken) {
    return {
      user,
      token,
      identity: {
        givenName: userFromToken.givenName,
        displayName: userFromToken.displayName || userFromToken.givenName,
      },
      isViewer,
      isAdmin: actorIsAdmin,
      isImpersonating: isImpersonating,
    };
  }

  if (storedUserName) {
    return {
      user,
      token,
      identity: {
        givenName: storedUserName,
        displayName: storedUserName,
      },
      isViewer,
      isAdmin: actorIsAdmin,
      isImpersonating: isImpersonating,
    };
  }

  return {
    user,
    token,
    identity: defaultIdentity,
    isViewer,
    isAdmin: actorIsAdmin,
    isImpersonating: isImpersonating,
  };
};

export const useAuthSessionStore = create<AuthSessionState>((set) => ({
  ...buildAuthSessionSnapshot(),
  sessionEpoch: 0,
  syncFromCookies: (options) => {
    set((state) => ({
      ...buildAuthSessionSnapshot(),
      sessionEpoch: options?.incrementEpoch
        ? state.sessionEpoch + 1
        : state.sessionEpoch,
    }));
  },
}));